import { Plus, ArrowDown, MessageCircle, Mail, Smartphone, MoreHorizontal, Clock } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { FilterBar } from "@/components/shared/FilterBar";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { cascades } from "@/data/mockData";

const iconFor = (c: string) => c === "WhatsApp" ? MessageCircle : c === "Email" ? Mail : Smartphone;

export default function Cascades() {
  const c = cascades[0];
  return (
    <div className="space-y-6">
      <PageHeader
        title="Cascades"
        description="Fall back across channels when delivery fails (WhatsApp -> SMS -> Email)."
        actions={<Button size="sm"><Plus className="h-4 w-4" />Add cascade</Button>}
      />

      <FilterBar searchPlaceholder="Search cascades..." />

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Name</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-left font-medium">Channel sequence</th>
              <th className="px-4 py-3 text-left font-medium">Fallback timeout</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {cascades.map((csc) => (
              <tr key={csc.id} className="hover:bg-muted/40">
                <td className="px-4 py-3">
                  <div className="font-medium">{csc.name}</div>
                  <div className="font-mono text-xs text-muted-foreground">{csc.id}</div>
                </td>
                <td className="px-4 py-3"><StatusBadge status={csc.status} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {csc.channels.map((ch, i) => {
                      const Icon = iconFor(ch);
                      return (
                        <div key={i} className="flex items-center gap-2">
                          <div className="flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs">
                            <Icon className="h-3.5 w-3.5 text-primary" /> {ch}
                          </div>
                          {i < csc.channels.length - 1 && <span className="text-muted-foreground">&rarr;</span>}
                        </div>
                      );
                    })}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{csc.timeout}</td>
                <td className="px-2 py-3"><Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-lg border border-border bg-card p-6">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Visual builder - {c.name}</h2>
          <Button variant="outline" size="sm">Save changes</Button>
        </div>
        <p className="text-xs text-muted-foreground">If a step times out, the next channel is attempted.</p>
        <div className="mx-auto mt-6 max-w-md space-y-2">
          {c.channels.map((ch, i) => {
            const Icon = iconFor(ch);
            return (
              <div key={i}>
                <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 p-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary"><Icon className="h-5 w-5" /></div>
                  <div className="flex-1">
                    <div className="text-sm font-medium">Step {i + 1} - {ch}</div>
                    <div className="text-xs text-muted-foreground">Attempt delivery via {ch}</div>
                  </div>
                  <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                </div>
                {i < c.channels.length - 1 && (
                  <div className="my-1 flex items-center justify-center gap-2 py-1 text-xs text-muted-foreground">
                    <ArrowDown className="h-3 w-3" />
                    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5">
                      <Clock className="h-3 w-3" /> wait {c.timeout}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
          <button className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border py-3 text-xs font-medium text-muted-foreground hover:border-primary hover:text-primary">
            <Plus className="h-3.5 w-3.5" /> Add channel step
          </button>
        </div>
      </div>
    </div>
  );
}
